import { Button, PlusIcon, TextInput } from '@razorpay/blade/components';
import closeIcon from 'assets/icon--close-grey.svg';
import crossIcon from 'assets/icons/red_cross.png';
import { ALLOWANCES_LIMIT } from 'components/RunPayroll/constants';
import { useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { EarningsComponentType } from 'components/SalaryComponents/types';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { SecondaryButton } from 'components/ui/Button';
import { Input } from 'components/ui/Form/Input';
import { Table } from 'components/ui/Table';
import React from 'react';
import { getStaticMediaUrl } from 'utils/Urls';
import styles from '../../index.module.scss';
import useSalaryComponentsList from '../hooks/useSalaryComponentsList';
import { EditPayrollProps, PayrollAllowanceV2 } from '../types';
import {
  getAdditionAdhocPayments,
  getSearchDropdownDescription,
  isAdditionAdhocPayment,
} from '../utils';

type AdditionsProps = EditPayrollProps & {
  allowances: PayrollAllowanceV2[];
  onAllowanceChange: (updateId: string, updates: Partial<PayrollAllowanceV2>) => void;
  onAllowanceAdd: () => void;
  onAllowanceDelete: (deleteId: string) => void;
};

const AdditionsV2 = ({
  payrollEntry,
  allowances,
  onAllowanceChange,
  onAllowanceDelete,
  onAllowanceAdd,
  payrollMonth,
}: AdditionsProps) => {
  const { openEarningsCreation } = useSalaryComponentHelpers();

  const { searchSalaryComponents, isFetchingSalaryComponents } =
    useSalaryComponentsList<EarningsComponentType>({
      requestParams: {
        category: 'EARNINGS_CATEGORY',
        payType: 'LIST_ADHOC_TYPE',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const onHoverDeleteButton = (idx: number) => {
    const row = document.getElementById(`addition-row-${idx}`);
    row?.classList.add(styles['run-payroll-deduction-row-hover']);
  };

  const onMouseoutOfDeleteButton = (idx: number) => {
    const row = document.getElementById(`addition-row-${idx}`);
    row?.classList.remove(styles['run-payroll-deduction-row-hover']);
  };

  const adhocPaymentIds = getAdditionAdhocPayments(allowances).map((e) => e.id);

  const getAllowancesSearchSkipList = (
    allowances: PayrollAllowanceV2[],
    currentId: string,
  ): string[] => {
    return allowances
      .filter((allowance) => allowance.id !== '' && allowance.id !== currentId)
      .map((allowance) => allowance.id);
  };

  const shouldShowAsDisabledItem = (item: PayrollAllowanceV2) => {
    return isAdditionAdhocPayment(item);
  };

  return (
    <div>
      <Table
        className="w-full  border border-solid border-surfaceBorderGraySubtle"
        showAlternateColors={false}>
        <thead>
          <tr>
            <th>Label</th>
            <th>Amount</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {allowances.map((item, idx) => {
            return (
              <tr key={idx} id={`addition-row-${idx}`}>
                <td>
                  {shouldShowAsDisabledItem(item) ? (
                    <TextInput label="" value={`${item.name} *`} isDisabled />
                  ) : (
                    <AutoCompleteV2
                      options={searchSalaryComponents([
                        ...adhocPaymentIds,
                        ...getAllowancesSearchSkipList(allowances, item.id),
                      ])}
                      itemToKey={(item) => item.id}
                      itemToLabel={(item) => item.name}
                      itemToDescription={(item) => getSearchDropdownDescription(item) ?? ''}
                      data-testid={`allowance-${allowances[idx].name}-name`}
                      name={`name-${idx}`}
                      value={allowances[idx].id}
                      placeholder="Eg. Bonus, Travel Allowance etc."
                      isDisabled={isFetchingSalaryComponents}
                      onChange={(event) => {
                        onAllowanceChange(item.id, {
                          name: event.name,
                          id: event.id,
                        });
                      }}
                      onClearSelection={() => {
                        onAllowanceChange(item.id, {
                          name: '',
                          id: '',
                        });
                      }}
                      footer={
                        <Button
                          variant="tertiary"
                          size="small"
                          icon={PlusIcon}
                          onClick={openEarningsCreation}
                          isFullWidth>
                          Create addition
                        </Button>
                      }
                    />
                  )}
                </td>
                <td>
                  {shouldShowAsDisabledItem(item) ? (
                    <TextInput label="" value={item.amount.toString()} isDisabled />
                  ) : (
                    <Input
                      type="number"
                      data-testid={`allowance-${allowances[idx].name}-value`}
                      className={styles['run-payroll-edit-number']}
                      name={`amount-${idx}`}
                      value={allowances[idx].amount ? allowances[idx].amount : ''}
                      onChange={(event) => {
                        onAllowanceChange(item.id, { amount: Number(event.target.value) });
                      }}
                    />
                  )}
                </td>
                <td
                  onMouseOver={() =>
                    shouldShowAsDisabledItem(item) ? null : onHoverDeleteButton(idx)
                  }
                  onMouseOut={() =>
                    shouldShowAsDisabledItem(item) ? null : onMouseoutOfDeleteButton(idx)
                  }>
                  {shouldShowAsDisabledItem(item) ? null : (
                    <>
                      <img
                        className={styles['run-payroll-deduction-red-cross']}
                        src={getStaticMediaUrl(crossIcon)}
                        alt=""
                        onClick={() => onAllowanceDelete(item.id)}
                      />
                      <img
                        className={styles['run-payroll-deduction-grey-close']}
                        src={getStaticMediaUrl(closeIcon)}
                        alt=""
                        onClick={() => onAllowanceDelete(item.id)}
                      />
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div>
        {allowances.length < ALLOWANCES_LIMIT && (
          <SecondaryButton
            onClick={onAllowanceAdd}
            disabled={false}
            className={styles['run-payroll-new-deduction-button']}
            type="button">
            + New addition
          </SecondaryButton>
        )}
      </div>
      {payrollEntry.adhocAllowances && payrollEntry.adhocAllowances.length > 0 && (
        <div className="mt-5">* indicates one-time payment paid to employee.</div>
      )}
    </div>
  );
};

export default AdditionsV2;
